import { FC, useRef, useState } from 'react';
import { CheckIcon, PdfFileIcon } from '@icons';

import {
  acceptedInputTypes,
  allowedFileTypes
} from '@components/file-upload/constants';
import { Icon } from '@components/index';
import useDragAndDrop from '@hooks/useDragAndDrop';
import { EventDocumentUploadProps } from '@types';
import { bytesToMB } from '@utils/file';

const DocumentUpload: FC<EventDocumentUploadProps> = ({
  label,
  handleAdd,
  handleRemove,
  uploadedFile,
  allowedFileSize,
  allowedFileSizeLabel,
  acceptedDocInputTypes = acceptedInputTypes,
  allowedDocumentTypes = allowedFileTypes,
  subText = '',
  onError,
  uploadedFileUrl,
  className,
  iconClassName,
  hideSuccessState = false,
  errorStyles = 'text-sm text-error',
  customClassName = ''
}) => {
  const inputId = `${label}_input_id`;
  const {
    dragOver,
    setDragOver,
    onDragOver,
    onDragLeave,
    fileDropError,
    setFileDropError
  } = useDragAndDrop();
  const [file, setFile] = useState<File | undefined | null>(uploadedFile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidFile = (selectedFile: File) => {
    if (allowedFileSize && selectedFile?.size > allowedFileSize) {
      const errorMessage = `Upload failed. File size more than ${allowedFileSizeLabel}`;
      setFileDropError(errorMessage);
      onError?.(errorMessage);
      return false;
    }
    if (!allowedDocumentTypes.includes(selectedFile.type)) {
      const errorMessage = 'Upload failed. Unsupported file type';
      setFileDropError(errorMessage);
      onError?.(errorMessage);
      return false;
    }
    return true;
  };

  const handleFileProcessing = async (selectedFile: File) => {
    setFileDropError('');
    if (!hideSuccessState) {
      setFile(selectedFile);
    }
    handleAdd(selectedFile);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile && isValidFile(selectedFile)) {
      handleFileProcessing(selectedFile);
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const selectedFile = e?.dataTransfer?.files[0];
    if (isValidFile(selectedFile)) {
      handleFileProcessing(selectedFile);
    }
  };

  const deleteFile = () => {
    setFile(undefined);
    handleRemove();
  };

  if (file) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-y-4 rounded-md border border-dashed border-grey">
        <CheckIcon />
        <div className="text-sm font-bold text-content">
          Uploaded successfully!
        </div>
        <div className="flex items-center gap-x-2">
          <div className="text-sm font-bold text-grey">{file.name}</div>
          <div className="size-1.5 rounded-full bg-grey" />
          <div className="text-sm font-bold text-grey">
            {bytesToMB(file?.size)}
          </div>
          <div className="h-3.5 w-0.5 bg-grey40" />
          <div
            className="cursor-pointer text-sm font-bold text-error40"
            role="presentation"
            onClick={deleteFile}
          >
            Cancel
          </div>
        </div>
      </div>
    );
  }
  if (uploadedFileUrl) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-y-4 rounded-md border border-dashed border-grey">
        <PdfFileIcon />
        <div className="text-sm font-bold text-content">Uploaded File</div>
        <div className="flex items-center gap-x-2">
          <a
            className="cursor-pointer text-sm font-bold text-primary"
            href={uploadedFileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </a>
          <div className="h-3.5 w-0.5 bg-grey40" />
          <div
            className="cursor-pointer text-sm font-bold text-error40"
            role="presentation"
            onClick={deleteFile}
          >
            Delete
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <input
        type="file"
        id={inputId}
        className="hidden"
        onChange={onFileSelect}
        accept={acceptedDocInputTypes}
        ref={fileInputRef}
      />
      <div
        className={`flex h-40 flex-col items-center justify-center rounded-md border border-dashed border-grey ${className} ${
          dragOver ? 'bg-grey70' : ''
        } ${fileDropError ? '!border-solid !border-error' : ''} `}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <Icon
          name="upload"
          size="none"
          className={`mb-4 size-10 ${iconClassName}`}
        />
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <div className={customClassName}>
              <label
                htmlFor={inputId}
                className="cursor-pointer font-medium text-primary"
              >
                Click to upload{' '}
              </label>
              <span className="font-medium text-grey">or drag and drop</span>
            </div>
            {subText && <div className="text-sm text-grey">{subText}</div>}
          </div>
        </div>
      </div>
      {fileDropError && (
        <div className={`mt-3 rounded bg-error5 px-4 py-1 ${errorStyles}`}>
          {fileDropError}
        </div>
      )}
    </>
  );
};
export default DocumentUpload;
